(() => {
    const config = {
        selectors: {
            container: '[data-article-layout]',
            body: '[data-article-body]',
            toc: '[data-article-toc]',
            tocList: '[data-toc-list]',
            copyButton: '[data-copy-code]',
            sidebarToggle: '[data-article-sidebar-toggle]',
            sidebarDrawer: '[data-article-sidebar-drawer]',
            sidebarOverlay: '[data-article-sidebar-overlay]',
            sidebarClose: '[data-article-sidebar-close]',
            sidebarPanel: '[data-article-sidebar-panel]',
            sidebarContent: '[data-article-sidebar-content]'
        },
        toc: {
            headingsAttr: 'tocHeadings'
        },
        sidebar: {
            mobileMedia: '(max-width: 989px)',
            toggleScrollThreshold: 500,
            scrollLockClass: 'has-article-sidebar-open'
        }
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const toArray = (collection) => (Array.isArray(collection) ? collection : Array.from(collection || []));

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    const markInitialized = (element, key = 'initialized') => {
        if (!element) {
            return false;
        }
        if (element.dataset[key] === 'true') {
            return false;
        }
        element.dataset[key] = 'true';
        return true;
    };

    const tocStates = new WeakMap();

    const getTocState = (container) => {
        if (!container) {
            return null;
        }
        if (!tocStates.has(container)) {
            tocStates.set(container, {
                initialized: false,
                listenersAttached: false,
                tocItems: [],
                activeHeadingId: '',
                pendingManualTargetId: null,
                ensureActiveLinkVisible: null,
                setActiveLink: null,
                evaluateActiveHeading: null,
                handleScroll: null
            });
        }
        return tocStates.get(container);
    };

    const withAnimationFrame = (callback) => {
        let ticking = false;
        return () => {
            if (ticking) {
                return;
            }
            ticking = true;
            window.requestAnimationFrame(() => {
                callback();
                ticking = false;
            });
        };
    };

    const slugifyHeading = (value) => {
        if (!value) {
            return '';
        }
        return value
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/[\s-]+/g, '-');
    };

    const createUniqueId = (base, usedIds) => {
        if (!base) {
            return '';
        }
        let nextId = base;
        let suffix = 1;
        while (usedIds.has(nextId)) {
            nextId = `${base}-${suffix}`;
            suffix += 1;
        }
        usedIds.add(nextId);
        return nextId;
    };

    const smoothScrollTo = (target) => {
        if (!target) {
            return;
        }
        const rect = target.getBoundingClientRect();
        const headingCenter = window.scrollY + rect.top + (rect.height / 2);
        const desiredScrollTop = headingCenter - (window.innerHeight / 2);
        const maxScrollTop = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
        const constrainedScrollTop = clamp(desiredScrollTop, 0, maxScrollTop);
        window.scrollTo({ top: constrainedScrollTop, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    };

    const initCopyButtons = (container) => {
        toArray(container.querySelectorAll(config.selectors.copyButton)).forEach((button) => {
            if (!markInitialized(button, 'copyInitialized')) {
                return;
            }
            const value = button.dataset.copyValue || button.textContent.trim();
            if (!value) {
                return;
            }
            const successLabel = button.dataset.copySuccess || '';
            const labelSpan = button.querySelector('span');
            if (labelSpan) {
                labelSpan.classList.add('eeg-article-banner__cta-label');
            }
            const originalLabel = labelSpan ? labelSpan.textContent.trim() : '';
            if (originalLabel && !button.dataset.originalLabel) {
                button.dataset.originalLabel = originalLabel;
            }
            let successSpan = button.querySelector('.eeg-article-banner__cta-success');
            if (!successSpan) {
                successSpan = document.createElement('span');
                successSpan.className = 'eeg-article-banner__cta-success';
                button.appendChild(successSpan);
            }
            const initialSuccessText = successLabel || originalLabel || '';
            successSpan.textContent = initialSuccessText;
            successSpan.setAttribute('aria-hidden', 'true');
            if (!button.dataset.originalAriaLabel) {
                const initialAria = button.getAttribute('aria-label') || originalLabel || '';
                if (initialAria) {
                    button.dataset.originalAriaLabel = initialAria;
                }
            }
            button.setAttribute('aria-live', 'polite');

            let feedbackTimeout;

            const applySuccessState = () => {
                const fallbackLabel = button.dataset.originalLabel || labelSpan?.textContent.trim() || '';
                const successText = successLabel || fallbackLabel;
                successSpan.textContent = successText;
                successSpan.setAttribute('aria-hidden', 'false');
                if (successText) {
                    button.setAttribute('aria-label', successText);
                }
                button.classList.add('is-success');
                if (feedbackTimeout) {
                    clearTimeout(feedbackTimeout);
                }
                feedbackTimeout = setTimeout(() => {
                    successSpan.setAttribute('aria-hidden', 'true');
                    button.classList.remove('is-success');
                    const restoreAria = button.dataset.originalAriaLabel || fallbackLabel;
                    if (restoreAria) {
                        button.setAttribute('aria-label', restoreAria);
                    }
                }, 3000);
            };

            const handleCopy = () => {
                const copyPromise = (navigator.clipboard && navigator.clipboard.writeText)
                    ? navigator.clipboard.writeText(value)
                    : Promise.reject(new Error('clipboard_unavailable'));

                copyPromise.catch(() => {
                    const textarea = document.createElement('textarea');
                    textarea.value = value;
                    textarea.setAttribute('readonly', '');
                    textarea.style.position = 'absolute';
                    textarea.style.left = '-9999px';
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                }).finally(applySuccessState);
            };

            button.addEventListener('click', handleCopy);
            button.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleCopy();
                }
            });
        });
    };

    const initTableOfContents = (container, { force = false } = {}) => {
        const state = getTocState(container);
        if (!state) {
            return;
        }
        if (state.initialized && !force) {
            return;
        }

        const tocEnabled = container.dataset.tocEnabled === 'true';
        if (!tocEnabled) {
            return;
        }

        const body = container.querySelector(config.selectors.body);
        const sectionRoot = container.closest('section') || container;
        const tocElements = toArray(sectionRoot.querySelectorAll(config.selectors.toc));
        const tocLists = tocElements
            .map((tocElement) => tocElement.querySelector(config.selectors.tocList))
            .filter(Boolean);

        if (!body || !tocElements.length || !tocLists.length) {
            return;
        }

        tocLists.forEach((list) => {
            list.innerHTML = '';
        });

        const headingSelector = container.dataset[config.toc.headingsAttr] || 'h2, h3, h4';
        const headings = toArray(body.querySelectorAll(headingSelector))
            .filter((heading) => heading.textContent.trim().length > 0);

        if (!headings.length) {
            tocElements.forEach((tocElement) => {
                tocElement.setAttribute('hidden', 'hidden');
            });
            container.dataset.tocEnabled = 'false';
            state.tocItems = [];
            state.initialized = true;
            return;
        }

        const usedIds = new Set(
            toArray(document.querySelectorAll('[id]'))
                .map((element) => element.getAttribute('id'))
                .filter(Boolean)
        );

        const tocItems = [];

        headings.forEach((heading) => {
            const headingText = heading.textContent.trim();
            if (!headingText) {
                return;
            }

            let headingId = heading.getAttribute('id');
            if (headingId) {
                headingId = headingId.trim();
            }
            if (!headingId) {
                const slugBase = slugifyHeading(headingText) || 'heading';
                headingId = createUniqueId(slugBase, usedIds);
                heading.setAttribute('id', headingId);
            }

            const level = clamp(parseInt(heading.tagName.replace('H', ''), 10) - 1, 1, 3);

            const linkEntries = tocLists.map((list, listIndex) => {
                const listItem = document.createElement('li');
                listItem.className = `eeg-article-toc__list-item level-${level}`;

                const link = document.createElement('a');
                link.setAttribute('href', `#${headingId}`);
                link.dataset.headingTarget = headingId;
                link.textContent = headingText;

                listItem.appendChild(link);
                list.appendChild(listItem);

                return {
                    link,
                    list,
                    toc: tocElements[listIndex],
                    nav: link.closest('.eeg-article-toc__nav')
                };
            });

            tocItems.push({
                heading,
                targetId: headingId,
                links: linkEntries
            });
        });

        if (!tocItems.length) {
            tocElements.forEach((tocElement) => {
                tocElement.setAttribute('hidden', 'hidden');
            });
            container.dataset.tocEnabled = 'false';
            state.tocItems = [];
            state.initialized = true;
            return;
        }

        tocElements.forEach((tocElement) => {
            tocElement.removeAttribute('hidden');
        });

        state.tocItems = tocItems;
        state.activeHeadingId = '';
        state.pendingManualTargetId = null;

        if (!state.ensureActiveLinkVisible) {
            state.ensureActiveLinkVisible = (linkEntries) => {
                if (!Array.isArray(linkEntries)) {
                    return;
                }
                linkEntries.forEach(({ link, list, toc, nav }) => {
                    if (!link || !toc) {
                        return;
                    }
                    const candidates = [];
                    if (nav) {
                        candidates.push(nav);
                    }
                    if (list && list !== nav) {
                        candidates.push(list);
                    }
                    let scrollContainer = null;
                    for (const candidate of candidates) {
                        if (!candidate) {
                            continue;
                        }
                        const styles = window.getComputedStyle(candidate);
                        const overflowY = (styles.overflowY || '').toLowerCase();
                        const hasOverflow = candidate.scrollHeight > candidate.clientHeight + 1;
                        if (hasOverflow && (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')) {
                            scrollContainer = candidate;
                            break;
                        }
                    }
                    if (!scrollContainer) {
                        scrollContainer = candidates.find((candidate) => candidate && candidate.scrollHeight > candidate.clientHeight + 1) || candidates[0];
                    }
                    if (!scrollContainer) {
                        return;
                    }
                    const containerStyles = window.getComputedStyle(scrollContainer);
                    if (containerStyles.display === 'none' || containerStyles.visibility === 'hidden') {
                        return;
                    }
                    const tocStyles = window.getComputedStyle(toc);
                    if (tocStyles.display === 'none' || tocStyles.visibility === 'hidden') {
                        return;
                    }
                    const drawer = toc.closest(config.selectors.sidebarDrawer);
                    if (drawer && drawer.getAttribute('aria-hidden') === 'true') {
                        return;
                    }

                    const item = link.closest('.eeg-article-toc__list-item') || link;
                    if (!item) {
                        return;
                    }
                    const itemRect = item.getBoundingClientRect();
                    const containerRect = scrollContainer.getBoundingClientRect();
                    const containerScrollTop = scrollContainer.scrollTop || 0;
                    const containerHeight = scrollContainer.clientHeight || 0;
                    if (containerHeight <= 0) {
                        return;
                    }
                    const itemCenterOffset = (itemRect.top - containerRect.top) + (itemRect.height / 2);
                    const desiredScrollTop = containerScrollTop + itemCenterOffset - (containerHeight / 2);
                    const maxScrollTop = Math.max(scrollContainer.scrollHeight - containerHeight, 0);
                    const nextScrollTop = Math.min(Math.max(desiredScrollTop, 0), maxScrollTop);
                    if (Math.abs(nextScrollTop - containerScrollTop) < 1) {
                        return;
                    }
                    if (typeof scrollContainer.scrollTo === 'function') {
                        scrollContainer.scrollTo({ top: nextScrollTop, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
                    } else {
                        scrollContainer.scrollTop = nextScrollTop;
                    }
                });
            };

            state.setActiveLink = (target) => {
                const targetId = target?.id || '';
                if (targetId === state.activeHeadingId) {
                    if (targetId && state.pendingManualTargetId === targetId) {
                        state.pendingManualTargetId = null;
                        const matchedItem = state.tocItems.find((item) => item.targetId === targetId);
                        if (matchedItem) {
                            state.ensureActiveLinkVisible(matchedItem.links);
                        }
                    }
                    return;
                }
                state.activeHeadingId = targetId;
                let activeItem = null;
                state.tocItems.forEach((item) => {
                    const isActive = item.targetId === targetId;
                    item.links.forEach(({ link }) => {
                        link.classList.toggle('is-active', isActive);
                        const listItem = link.closest('.eeg-article-toc__list-item');
                        if (listItem) {
                            listItem.classList.toggle('is-active', isActive);
                        }
                    });
                    if (isActive) {
                        activeItem = item;
                    }
                });
                if (!activeItem) {
                    return;
                }
                if (state.pendingManualTargetId === targetId) {
                    state.pendingManualTargetId = null;
                    state.ensureActiveLinkVisible(activeItem.links);
                } else if (state.pendingManualTargetId === null) {
                    state.ensureActiveLinkVisible(activeItem.links);
                }
            };

            const isNearBottom = () => {
                const scrollBottom = window.innerHeight + window.pageYOffset;
                const docHeight = document.documentElement.scrollHeight;
                return docHeight - scrollBottom <= 2;
            };

            const getHeadingPosition = (heading) => heading.getBoundingClientRect().top + window.pageYOffset;

            state.evaluateActiveHeading = () => {
                if (!state.tocItems.length) {
                    return;
                }

                const viewportCenter = window.pageYOffset + (window.innerHeight / 2);
                let currentHeading = state.tocItems[0].heading;
                let smallestDistance = Infinity;

                state.tocItems.forEach(({ heading }) => {
                    const headingCenter = getHeadingPosition(heading) + (heading.offsetHeight / 2);
                    const distance = Math.abs(headingCenter - viewportCenter);
                    if (distance < smallestDistance) {
                        smallestDistance = distance;
                        currentHeading = heading;
                    }
                });

                if (isNearBottom()) {
                    currentHeading = state.tocItems[state.tocItems.length - 1].heading;
                }

                state.setActiveLink(currentHeading);
            };
        }

        if (!state.listenersAttached) {
            state.handleScroll = withAnimationFrame(() => {
                if (state.evaluateActiveHeading) {
                    state.evaluateActiveHeading();
                }
            });
            window.addEventListener('scroll', state.handleScroll, { passive: true });
            window.addEventListener('resize', () => {
                if (state.evaluateActiveHeading) {
                    state.evaluateActiveHeading();
                }
            });
            window.addEventListener('load', () => {
                if (state.evaluateActiveHeading) {
                    state.evaluateActiveHeading();
                }
            });
            state.listenersAttached = true;
        }

        if (state.evaluateActiveHeading) {
            state.evaluateActiveHeading();
        }

        tocItems.forEach(({ links, heading, targetId }) => {
            links.forEach(({ link }) => {
                link.addEventListener('click', (event) => {
                    event.preventDefault();
                    state.pendingManualTargetId = heading.id || targetId || link.dataset.headingTarget || null;
                    smoothScrollTo(heading);
                });
            });
        });

        state.initialized = true;
    };

    const initSidebarDrawer = (container) => {
        if (!markInitialized(container, 'sidebarInitialized')) {
            return;
        }

        const toggle = document.querySelector(`${config.selectors.sidebarToggle}[data-article-sidebar-toggle-owner="${container.dataset.sectionId || container.id || ''}"]`)
            || container.querySelector(config.selectors.sidebarToggle);
        const drawer = document.querySelector(`${config.selectors.sidebarDrawer}#eeg-article-sidebar-drawer-${container.dataset.sectionId || container.id || ''}`)
            || container.querySelector(config.selectors.sidebarDrawer);
        if (!toggle || !drawer) {
            return;
        }

        const overlay = drawer.querySelector(config.selectors.sidebarOverlay);
        const panel = drawer.querySelector(config.selectors.sidebarPanel);
        const content = drawer.querySelector(config.selectors.sidebarContent);
        const closeButtons = drawer.querySelectorAll(config.selectors.sidebarClose);
        if (!overlay || !panel || !content) {
            return;
        }
        const ownerId = toggle.dataset.articleSidebarToggleOwner
            || container.dataset.sectionId
            || container.id
            || 'article-sidebar';
        toggle.dataset.articleSidebarToggleOwner = ownerId;

        const initialIsMobile = window.matchMedia(config.sidebar.mobileMedia).matches;
        drawer.setAttribute(
            'aria-hidden',
            initialIsMobile ? (drawer.classList.contains('is-open') ? 'false' : 'true') : 'false'
        );

        const body = document.body;
        const focusableSelector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(',');

        let lastFocusedElement = null;

        const getFocusable = () => {
            return Array.from(panel.querySelectorAll(focusableSelector)).filter((element) => {
                if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') {
                    return false;
                }
                const styles = window.getComputedStyle(element);
                if (styles.visibility === 'hidden' || styles.display === 'none') {
                    return false;
                }
                return element.offsetWidth > 0 || element.offsetHeight > 0 || element === panel;
            });
        };

        const setBodyScrollLock = (locked) => {
            if (!body) {
                return;
            }
            body.classList.toggle(config.sidebar.scrollLockClass, locked);
        };

        const focusPanel = () => {
            window.requestAnimationFrame(() => {
                panel.focus({ preventScroll: true });
            });
        };

        const passiveCaptureOptions = { capture: true, passive: true };

        const closeDrawer = ({ restoreFocus = true } = {}) => {
            if (!drawer.classList.contains('is-open')) {
                return;
            }
            drawer.classList.remove('is-open');
            const isMobile = window.matchMedia(config.sidebar.mobileMedia).matches;
            drawer.setAttribute('aria-hidden', isMobile ? 'true' : 'false');
            toggle.setAttribute('aria-expanded', 'false');
            setBodyScrollLock(false);
            document.removeEventListener('keydown', handleKeydown);
            document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
            document.removeEventListener('click', handleOutsidePointerDown, true);
            document.removeEventListener('touchstart', handleOutsidePointerDown, passiveCaptureOptions);
            if (restoreFocus) {
                const target = lastFocusedElement && typeof lastFocusedElement.focus === 'function'
                    ? lastFocusedElement
                    : toggle;
                target.focus({ preventScroll: true });
            }
            lastFocusedElement = null;
            applyToggleVisibility();
        };

        const handleKeydown = (event) => {
            if (!drawer.classList.contains('is-open')) {
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                closeDrawer();
                return;
            }
            if (event.key !== 'Tab') {
                return;
            }
            const focusable = getFocusable();
            if (!focusable.length) {
                event.preventDefault();
                focusPanel();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        const handleOutsidePointerDown = (event) => {
            if (!drawer.classList.contains('is-open')) {
                return;
            }
            const target = event.target;
            if (panel.contains(target)) {
                return;
            }
            if (overlay.contains(target)) {
                return;
            }
            if (target === toggle || (toggle && toggle.contains(target))) {
                return;
            }
            closeDrawer();
        };

        const openDrawer = () => {
            if (drawer.classList.contains('is-open')) {
                return;
            }
            lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            drawer.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            toggle.setAttribute('aria-expanded', 'true');
            setBodyScrollLock(true);
            panel.scrollTop = 0;
            focusPanel();
            document.addEventListener('keydown', handleKeydown);
            const isMobile = window.matchMedia(config.sidebar.mobileMedia).matches;
            if (isMobile) {
                document.addEventListener('pointerdown', handleOutsidePointerDown, true);
                document.addEventListener('click', handleOutsidePointerDown, true);
                document.addEventListener('touchstart', handleOutsidePointerDown, passiveCaptureOptions);
            }
            applyToggleVisibility();
        };

        toggle.addEventListener('click', () => {
            if (drawer.classList.contains('is-open')) {
                closeDrawer({ restoreFocus: false });
            } else {
                openDrawer();
            }
        });

        overlay.addEventListener('click', () => {
            closeDrawer();
        });

        if (closeButtons.length) {
            closeButtons.forEach((button) => {
                button.addEventListener('click', () => {
                    closeDrawer();
                });
            });
        }

        const mediaQuery = window.matchMedia(config.sidebar.mobileMedia);
        let isMobileMatched = mediaQuery.matches;
        let allowScrollVisibility = false;

        const applyToggleVisibility = () => {
            const shouldBeVisible = isMobileMatched && allowScrollVisibility && !drawer.classList.contains('is-open');
            if (shouldBeVisible) {
                toggle.classList.add('visible');
            } else {
                toggle.classList.remove('visible');
                if (!drawer.classList.contains('is-open')) {
                    toggle.setAttribute('aria-expanded', 'false');
                }
            }
        };

        const evaluateScrollVisibility = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
            const nextAllow = scrollTop > config.sidebar.toggleScrollThreshold;
            if (nextAllow !== allowScrollVisibility) {
                allowScrollVisibility = nextAllow;
                applyToggleVisibility();
            }
        };

        const handleScrollForToggle = withAnimationFrame(evaluateScrollVisibility);

        const handleMediaQueryChange = (event) => {
            isMobileMatched = event.matches;
            if (!isMobileMatched) {
                closeDrawer({ restoreFocus: false });
            }
            applyToggleVisibility();
        };

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleMediaQueryChange);
        } else if (typeof mediaQuery.addListener === 'function') {
            mediaQuery.addListener(handleMediaQueryChange);
        }

        window.addEventListener('scroll', handleScrollForToggle, { passive: true });
        evaluateScrollVisibility();
        applyToggleVisibility();

        document.addEventListener('shopify:section:unload', (event) => {
            if (event.target?.contains(container)) {
                closeDrawer({ restoreFocus: false });
                if (typeof mediaQuery.removeEventListener === 'function') {
                    mediaQuery.removeEventListener('change', handleMediaQueryChange);
                } else if (typeof mediaQuery.removeListener === 'function') {
                    mediaQuery.removeListener(handleMediaQueryChange);
                }
                window.removeEventListener('scroll', handleScrollForToggle);
                document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
                document.removeEventListener('click', handleOutsidePointerDown, true);
                document.removeEventListener('touchstart', handleOutsidePointerDown, passiveCaptureOptions);
            }
        });

        applyToggleVisibility();
    };

    const initArticleSection = (container) => {
        if (!markInitialized(container, 'articleInitialized')) {
            return;
        }

        initCopyButtons(container);
        initTableOfContents(container);
        initSidebarDrawer(container);
    };

    const initAll = (scope = document) => {
        scope.querySelectorAll(config.selectors.container).forEach(initArticleSection);
    };

    document.addEventListener('DOMContentLoaded', () => {
        initAll();
    });

    document.addEventListener('shopify:section:load', (event) => {
        initAll(event.target);
    });

    document.addEventListener('shopify:section:select', (event) => {
        initAll(event.target);
    });
})();
